// =============================================
// Content Audit Service — 4. faza pipeline'u (po publish).
// Orchestrator dla 6 warstw audytu:
//   Layer 1: completeness (regex, FREE)
//   Layer 2: internal links (HTTP HEAD, FREE)
//   Layer 3: external links (HTTP HEAD, FREE)
//   Layer 4: SEO meta sanity (fetch live WP HTML, FREE)
//   Layer 5: schema.org JSON-LD parse + banned tokens (FREE)
//   Layer 6: LLM semantic audit (gpt-4.1-mini, ~$0.003/art) — opt-in
//
// Cache: jeśli content_hash + llm_included matchuje ostatni audit → reuse (skip).
// Persist: insert do content_audits (multi-run history).
// =============================================

import { supabase } from "@/integrations/supabase/client";
import { logPublic, logDebug } from "@/services/contentEngine/contentLogger";
import { auditCompleteness, type CompletenessResult } from "@/services/auditLayers/completenessAudit";
import { auditLinks, type LinksAuditResult } from "@/services/auditLayers/linksAudit";
import { auditSeoMetaFromYoast, type SeoMetaAuditResult } from "@/services/auditLayers/seoMetaAudit";
import { auditSchema, type SchemaAuditResult } from "@/services/auditLayers/schemaAudit";
import { auditLlm, type LlmAuditResult } from "@/services/auditLayers/llmAudit";

export interface ContentAuditOptions {
  includeLLM?: boolean; // default true
  forceRefresh?: boolean; // bypass cache
}

export interface ContentAuditResult {
  audit_id: string | null;
  node_id: string;
  audit_status: "passed" | "warning" | "failed";
  overall_score: number;
  llm_included: boolean;
  cached: boolean;
  layer_completeness: CompletenessResult | null;
  layer_internal_links: {
    checked: number;
    broken: number;
    issues: string[];
    status: string;
    links?: Array<{ url: string; anchor: string }>;
  } | null;
  layer_external_links: {
    checked: number;
    broken: number;
    issues: string[];
    status: string;
    links?: Array<{ url: string; anchor: string }>;
  } | null;
  layer_seo_meta: SeoMetaAuditResult | null;
  layer_schema: SchemaAuditResult | null;
  layer_llm: LlmAuditResult | null;
  cost_usd: number;
  audited_at: string;
}

interface NodeAuditContext {
  node_id: string;
  final_html: string;           // LIVE content z WP (post.content.rendered) — z schema injection
  seo_title: string | null;     // expected (z DB)
  meta_description: string | null; // expected (z DB)
  yoast_seo_title: string | null;   // LIVE z WP meta (Yoast)
  yoast_meta_description: string | null;
  published_at: string | null;
  effective_publish_date: string | null;
  wp_post_url: string | null;
  wp_post_id: number | null;
  wp_modified_at: string | null;
  site_url: string;
  user_id: string | null;
  /** Per-domena toggle dla schema.org injection (websites.schema_enabled).
   * Gdy false → warstwa schema w audycie POMIJANA (status null, nie liczy się
   * do agregatu ani score). Wzorzec spójny ze skip linków przy braku site_url. */
  schema_enabled: boolean | null;
}

// Status worst-of-all per warstwa → overall audit_status
function aggregateStatus(
  statuses: Array<"ok" | "warning" | "failed" | null>,
): "passed" | "warning" | "failed" {
  let hasWarning = false;
  for (const s of statuses) {
    if (s === "failed") return "failed";
    if (s === "warning") hasWarning = true;
  }
  return hasWarning ? "warning" : "passed";
}

// Simple weighted score 0-100:
//   Każda warstwa daje 0/50/100 points (failed/warning/ok), średnia.
function computeScore(
  statuses: Array<"ok" | "warning" | "failed" | null>,
): number {
  const points = statuses
    .filter((s): s is "ok" | "warning" | "failed" => s !== null)
    .map((s) => (s === "ok" ? 100 : s === "warning" ? 50 : 0));
  if (points.length === 0) return 0;
  return Math.round(points.reduce((a, b) => a + b, 0) / points.length);
}

async function sha256Hex(text: string): Promise<string> {
  // Browser-side SubtleCrypto (Node fallback przez globalThis.crypto.webcrypto)
  if (typeof globalThis.crypto !== "undefined" && globalThis.crypto.subtle) {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    const hash = await globalThis.crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }
  // Fallback: simple djb2 (nie cryptographic, ale wystarczy do cache invalidation)
  let h = 5381;
  for (let i = 0; i < text.length; i++) {
    h = ((h << 5) + h + text.charCodeAt(i)) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

async function fetchAuditContext(nodeId: string): Promise<NodeAuditContext | null> {
  const { data: content } = await (supabase as any)
    .from("thematic_node_content")
    .select(
      "node_id, final_html, seo_title, meta_description, published_at, effective_publish_date, wp_post_url, wp_post_id, updated_at",
    )
    .eq("node_id", nodeId)
    .single();
  if (!content) return null;

  // Site URL + WP credentials z mapy/website
  const { data: node } = await (supabase as any)
    .from("thematic_nodes")
    .select("map_id")
    .eq("id", nodeId)
    .single();
  if (!node) return null;

  const { data: map } = await (supabase as any)
    .from("thematic_maps")
    .select("website_id")
    .eq("id", node.map_id)
    .single();
  if (!map?.website_id) return null;

  const { data: website } = await (supabase as any)
    .from("websites")
    .select("url, login, password, api_key, schema_enabled")
    .eq("id", map.website_id)
    .single();

  const { data: session } = await supabase.auth.getSession();

  // === LIVE WP FETCH ===
  // Pobieramy artykuł Z LIVE WORDPRESS (po publikacji) — context=view zwraca rendered HTML
  // z schema injection + Yoast meta. Idzie przez nginx proxy /api/wordpress/ → same-origin,
  // brak CORS. To audyt OPUBLIKOWANEJ treści, nie DB pre-publish state.
  let liveHtml = content.final_html; // fallback do DB jeśli WP fetch fail
  let yoastSeoTitle: string | null = null;
  let yoastMetaDescription: string | null = null;
  let wpModifiedAt: string | null = content.updated_at || null;

  // Backfill wp_post_id gdy NULL — pre-existing bug w publishQueueService zapisywał NULL.
  // Pobierz po slugu z wp_post_url przez REST → update DB → użyj w fetchPostForAudit.
  let effectiveWpPostId = content.wp_post_id as number | null;
  if (!effectiveWpPostId && content.wp_post_url && website?.url && website?.login && (website?.api_key || website?.password)) {
    try {
      const slug = content.wp_post_url
        .replace(/\/$/, "")
        .split("/")
        .pop() || "";
      // Niektóre URL'e mają ?p=ID format (legacy permalink)
      const pMatch = content.wp_post_url.match(/[?&]p=(\d+)/);
      if (pMatch) {
        effectiveWpPostId = parseInt(pMatch[1], 10);
      } else if (slug) {
        const { wordpressService } = await import("@/services/wordpressService");
        const prevCreds = wordpressService.getCredentials();
        wordpressService.setCredentials({
          url: website.url.replace(/\/$/, ""),
          username: website.login,
          password: website.api_key || website.password,
        });
        try {
          const foundId = await wordpressService.findPostIdBySlug(slug);
          if (foundId) effectiveWpPostId = foundId;
        } finally {
          if (prevCreds) wordpressService.setCredentials(prevCreds);
        }
      }
      // Update DB jeśli znaleźliśmy ID
      if (effectiveWpPostId) {
        await (supabase as any)
          .from("thematic_node_content")
          .update({ wp_post_id: effectiveWpPostId })
          .eq("node_id", nodeId);
        logPublic("AUDIT", `Backfilled wp_post_id=${effectiveWpPostId} dla node ${nodeId}`);
      }
    } catch (e) {
      logPublic("AUDIT", `Backfill wp_post_id failed dla node ${nodeId}: ${e instanceof Error ? e.message : "unknown"}`);
    }
  }

  if (effectiveWpPostId && website?.url && website?.login && (website?.api_key || website?.password)) {
    try {
      const { wordpressService } = await import("@/services/wordpressService");
      // Restore-after pattern — save current credentials, set ours, restore
      const prevCreds = wordpressService.getCredentials();
      wordpressService.setCredentials({
        url: website.url.replace(/\/$/, ""),
        username: website.login,
        password: website.api_key || website.password,
      });
      try {
        const res = await wordpressService.fetchPostForAudit(effectiveWpPostId);
        if (res.success && res.data) {
          const post = res.data as Record<string, unknown>;
          const contentObj = post.content as { rendered?: string } | undefined;
          if (contentObj?.rendered) liveHtml = contentObj.rendered;
          const meta = post.meta as Record<string, unknown> | undefined;
          if (meta) {
            // Plugin "Meta API REST Support" zapisuje BOTH "_yoast_wpseo_title"
            // (protected) i "yoast_wpseo_title" (unprotected). Czytamy oba.
            // Plus Yoast Premium 24+ używa różnych kluczy. Sprawdzamy wszystkie warianty.
            const pickStr = (...keys: string[]): string | null => {
              for (const k of keys) {
                const v = meta[k];
                if (typeof v === "string" && v.trim()) return v;
              }
              return null;
            };
            yoastSeoTitle = pickStr(
              "_yoast_wpseo_title",
              "yoast_wpseo_title",
              "_yoast_seo_title",
              "yoast_seo_title",
            );
            yoastMetaDescription = pickStr(
              "_yoast_wpseo_metadesc",
              "yoast_wpseo_metadesc",
              "_yoast_seo_metadesc",
              "yoast_seo_metadesc",
              "_yoast_wpseo_meta-description",
            );
            // Log wszystkich kluczy meta — pomocne w debug gdy Yoast nie eksponuje
            const metaKeys = Object.keys(meta);
            logPublic(
              "AUDIT",
              `Live WP meta dla post ${effectiveWpPostId}: keys=[${metaKeys.join(",")}] yoast_title=${yoastSeoTitle ? "yes" : "no"} yoast_desc=${yoastMetaDescription ? "yes" : "no"}`,
            );
          } else {
            logPublic("AUDIT", `Live WP post ${effectiveWpPostId} bez 'meta' field — plugin Meta API REST Support może nie eksponować przy READ`);
          }
          if (post.modified_gmt) wpModifiedAt = post.modified_gmt as string;
          else if (post.modified) wpModifiedAt = post.modified as string;
          logPublic("AUDIT", `Live WP fetch OK post ${effectiveWpPostId}: html=${liveHtml.length}ch, ld+json=${/ld\+json/.test(liveHtml) ? "yes" : "no"}`);
        } else {
          logPublic("AUDIT", `WP fetch fail dla post ${effectiveWpPostId}: ${res.error || "unknown"} — fallback do DB final_html`);
        }
      } finally {
        // Restore poprzednie credentials żeby nie tknąć innych callerów
        if (prevCreds) wordpressService.setCredentials(prevCreds);
      }
    } catch (e) {
      logPublic("AUDIT", `WP credentials/import fail dla node ${nodeId}: ${e instanceof Error ? e.message : "unknown"} — fallback do DB`);
    }
  }

  return {
    node_id: nodeId,
    final_html: liveHtml,
    seo_title: content.seo_title || null,
    meta_description: content.meta_description || null,
    yoast_seo_title: yoastSeoTitle,
    yoast_meta_description: yoastMetaDescription,
    published_at: content.published_at || null,
    effective_publish_date: content.effective_publish_date || null,
    wp_post_url: content.wp_post_url || null,
    wp_post_id: effectiveWpPostId,
    wp_modified_at: wpModifiedAt,
    site_url: website?.url || "",
    user_id: session?.session?.user?.id || null,
    schema_enabled: website?.schema_enabled ?? null,
  };
}

async function loadCachedAudit(
  nodeId: string,
  contentHash: string,
  llmIncluded: boolean,
): Promise<ContentAuditResult | null> {
  const { data } = await (supabase as any)
    .from("content_audits")
    .select("*")
    .eq("node_id", nodeId)
    .eq("content_hash", contentHash)
    .eq("llm_included", llmIncluded)
    .order("audited_at", { ascending: false })
    .limit(1);

  if (!data || data.length === 0) return null;
  const row = data[0];
  // Sanity check: gdy llm_included=true ale layer_llm IS NULL → wadliwy stary audit
  // (sprzed fix writing_profiles SELECT). Pomijaj cache → wymuszamy fresh audit z LLM.
  if (llmIncluded && row.layer_llm === null) {
    return null;
  }
  return {
    audit_id: row.id,
    node_id: row.node_id,
    audit_status: row.audit_status,
    overall_score: row.overall_score || 0,
    llm_included: row.llm_included,
    cached: true,
    layer_completeness: row.layer_completeness,
    layer_internal_links: row.layer_internal_links,
    layer_external_links: row.layer_external_links,
    layer_seo_meta: row.layer_seo_meta,
    layer_schema: row.layer_schema,
    layer_llm: row.layer_llm,
    cost_usd: parseFloat(row.cost_usd || "0"),
    audited_at: row.audited_at,
  };
}

export async function auditNode(
  nodeId: string,
  options: ContentAuditOptions = {},
): Promise<ContentAuditResult> {
  const includeLLM = options.includeLLM !== false;
  const forceRefresh = options.forceRefresh === true;

  const ctx = await fetchAuditContext(nodeId);
  if (!ctx) {
    throw new Error(`Audyt: brak final_html / mapy / website dla node ${nodeId}`);
  }

  const contentHash = await sha256Hex(ctx.final_html);

  // Cache check
  if (!forceRefresh) {
    const cached = await loadCachedAudit(nodeId, contentHash, includeLLM);
    if (cached) {
      logDebug("AUDIT", `Cache hit dla node ${nodeId} (status=${cached.audit_status})`);
      return cached;
    }
  }

  logPublic("AUDIT", `Audyt node ${nodeId}, LLM=${includeLLM ? "ON" : "OFF"}`);

  // Layers 1-5 równolegle (independent) — wszystkie operują na LIVE WP content (ctx.final_html
  // teraz zawiera rendered HTML z post.content.rendered, włącznie z schema injection).
  const expectedDatePublished = ctx.effective_publish_date || ctx.published_at;

  const [completenessR, linksR, schemaR, seoMetaR] = await Promise.all([
    Promise.resolve(auditCompleteness(ctx.final_html)),
    ctx.site_url ? auditLinks(ctx.final_html, ctx.site_url) : Promise.resolve(null),
    // Skip warstwy schema gdy domena ma schema_enabled=false (injection w generateSchemasForNode
    // też jest pomijany) — koniec false-positive FAILED na domenach bez schemy.
    // Wzorzec spójny ze skip linków przy braku site_url.
    ctx.schema_enabled === false
      ? Promise.resolve(null)
      : Promise.resolve(auditSchema(ctx.final_html, expectedDatePublished)),
    // SEO meta z REST (Yoast plugin) — bez fetch HTML, bez CORS
    Promise.resolve(
      auditSeoMetaFromYoast(
        ctx.yoast_seo_title,
        ctx.yoast_meta_description,
        ctx.seo_title,
        ctx.meta_description,
      ),
    ),
  ]);

  // Layer 6 — opt-in, sekwencyjne (osobne API call)
  let llmR: LlmAuditResult | null = null;
  if (includeLLM) {
    try {
      logPublic("AUDIT", `Layer 6 LLM start dla node ${nodeId}`);
      llmR = await auditLlm(nodeId);
      if (!llmR) {
        logPublic("AUDIT", `Layer 6 LLM dla node ${nodeId} zwrócił null (fetchLlmAuditInput failed — brak final_html/node/content)`);
      } else {
        logPublic("AUDIT", `Layer 6 LLM dla node ${nodeId} OK: status=${llmR.overall_status}, cost=$${llmR.cost_usd.toFixed(4)}, tokens_in=${llmR.tokens_in}, tokens_out=${llmR.tokens_out}`);
      }
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      const errStack = err instanceof Error && err.stack ? err.stack.slice(0, 500) : "";
      logPublic("AUDIT", `Layer 6 LLM EXCEPTION dla node ${nodeId}: ${errMsg}${errStack ? ` | stack: ${errStack}` : ""}`);
      // LLM failure nie blokuje audytu — pomijamy LLM, reszta layers leci dalej
    }
  }

  // Aggregate
  const statuses: Array<"ok" | "warning" | "failed" | null> = [
    completenessR.status,
    linksR ? (linksR as LinksAuditResult).status : null,
    linksR ? (linksR as LinksAuditResult).status : null, // internal + external w jednym status (linksAudit już worst-of)
    seoMetaR ? seoMetaR.status : null,
    schemaR ? schemaR.status : null, // null gdy ctx.schema_enabled === false (domena bez schemy)
    llmR ? llmR.status : null,
  ];
  const audit_status = aggregateStatus(statuses);
  const overall_score = computeScore(statuses);

  // Per-warstwa wyniki dla DB — pełna lista linków (url + anchor) żeby UI mógł
  // pokazać "do czego linkuje + jaki anchor" bez re-fetchu.
  const layer_completeness = completenessR;
  const layer_internal_links = linksR
    ? {
        checked: (linksR as LinksAuditResult).internal.checked,
        broken: (linksR as LinksAuditResult).internal.broken,
        issues: (linksR as LinksAuditResult).internal.issues,
        status: (linksR as LinksAuditResult).status,
        links: (linksR as LinksAuditResult).internal.results.map((r) => ({
          url: r.url,
          anchor: r.anchor_text,
        })),
      }
    : null;
  const layer_external_links = linksR
    ? {
        checked: (linksR as LinksAuditResult).external.checked,
        broken: (linksR as LinksAuditResult).external.broken,
        issues: (linksR as LinksAuditResult).external.issues,
        status: (linksR as LinksAuditResult).status,
        links: (linksR as LinksAuditResult).external.results.map((r) => ({
          url: r.url,
          anchor: r.anchor_text,
        })),
      }
    : null;
  const layer_seo_meta = seoMetaR;
  const layer_schema = schemaR;
  const layer_llm = llmR;

  const cost_usd = llmR ? llmR.cost_usd : 0;

  // Persist
  const auditedAt = new Date().toISOString();
  const { data: inserted, error } = await (supabase as any)
    .from("content_audits")
    .insert({
      node_id: nodeId,
      user_id: ctx.user_id,
      audit_status,
      overall_score,
      llm_included: includeLLM,
      layer_completeness,
      layer_internal_links,
      layer_external_links,
      layer_seo_meta,
      layer_schema,
      layer_llm,
      audited_at: auditedAt,
      cost_usd,
      llm_tokens_in: llmR?.tokens_in ?? null,
      llm_tokens_out: llmR?.tokens_out ?? null,
      content_hash: contentHash,
      wp_modified_at: ctx.wp_modified_at,
    })
    .select("id")
    .single();

  if (error) {
    logPublic("AUDIT", `DB insert failed: ${error.message || error}`);
  }

  return {
    audit_id: inserted?.id || null,
    node_id: nodeId,
    audit_status,
    overall_score,
    llm_included: includeLLM,
    cached: false,
    layer_completeness,
    layer_internal_links,
    layer_external_links,
    layer_seo_meta,
    layer_schema,
    layer_llm,
    cost_usd,
    audited_at: auditedAt,
  };
}

export interface BulkAuditProgress {
  nodeId: string;
  status: "ok" | "error";
  error?: string;
  result?: ContentAuditResult;
}

export async function auditBulk(
  nodeIds: string[],
  options: ContentAuditOptions = {},
  onProgress?: (p: BulkAuditProgress) => void,
): Promise<{ results: ContentAuditResult[]; failed: number; totalCost: number }> {
  const results: ContentAuditResult[] = [];
  let failed = 0;
  let totalCost = 0;

  // Concurrency 3 — żeby nie ddosować WP fetch + nie spamować OpenAI rate limit
  const CONCURRENCY = 3;
  let cursor = 0;

  async function worker(): Promise<void> {
    while (cursor < nodeIds.length) {
      const idx = cursor++;
      const nodeId = nodeIds[idx];
      try {
        const result = await auditNode(nodeId, options);
        results.push(result);
        totalCost += result.cost_usd;
        onProgress?.({ nodeId, status: "ok", result });
      } catch (err) {
        failed++;
        onProgress?.({
          nodeId,
          status: "error",
          error: err instanceof Error ? err.message : "unknown",
        });
      }
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, nodeIds.length) }, () => worker()),
  );

  return { results, failed, totalCost };
}

export async function fetchLatestAuditForNode(nodeId: string): Promise<ContentAuditResult | null> {
  const { data } = await (supabase as any)
    .from("content_audits")
    .select("*")
    .eq("node_id", nodeId)
    .order("audited_at", { ascending: false })
    .limit(1);
  if (!data || data.length === 0) return null;
  const row = data[0];
  return {
    audit_id: row.id,
    node_id: row.node_id,
    audit_status: row.audit_status,
    overall_score: row.overall_score || 0,
    llm_included: row.llm_included,
    cached: false,
    layer_completeness: row.layer_completeness,
    layer_internal_links: row.layer_internal_links,
    layer_external_links: row.layer_external_links,
    layer_seo_meta: row.layer_seo_meta,
    layer_schema: row.layer_schema,
    layer_llm: row.layer_llm,
    cost_usd: parseFloat(row.cost_usd || "0"),
    audited_at: row.audited_at,
  };
}

export async function fetchLatestAuditsForMap(
  mapId: string,
): Promise<Map<string, ContentAuditResult>> {
  // Fetch wszystkie nodes z mapy + ich latest audit
  const { data: nodes } = await (supabase as any)
    .from("thematic_nodes")
    .select("id")
    .eq("map_id", mapId);
  if (!nodes || nodes.length === 0) return new Map();

  const nodeIds = (nodes as Array<{ id: string }>).map((n) => n.id);
  const { data } = await (supabase as any)
    .from("content_audits")
    .select("*")
    .in("node_id", nodeIds)
    .order("audited_at", { ascending: false });

  const latestByNode = new Map<string, ContentAuditResult>();
  for (const row of (data || []) as Array<Record<string, unknown>>) {
    const nid = row.node_id as string;
    if (latestByNode.has(nid)) continue; // już mamy nowszy (dzięki sort desc)
    latestByNode.set(nid, {
      audit_id: row.id as string,
      node_id: nid,
      audit_status: row.audit_status as "passed" | "warning" | "failed",
      overall_score: (row.overall_score as number) || 0,
      llm_included: row.llm_included as boolean,
      cached: false,
      layer_completeness: row.layer_completeness as CompletenessResult | null,
      layer_internal_links: row.layer_internal_links as ContentAuditResult["layer_internal_links"],
      layer_external_links: row.layer_external_links as ContentAuditResult["layer_external_links"],
      layer_seo_meta: row.layer_seo_meta as SeoMetaAuditResult | null,
      layer_schema: row.layer_schema as SchemaAuditResult | null,
      layer_llm: row.layer_llm as LlmAuditResult | null,
      cost_usd: parseFloat((row.cost_usd as string) || "0"),
      audited_at: row.audited_at as string,
    });
  }
  return latestByNode;
}
